// pfs-field-group.ts — der KONTEXT für <pfs-field> (PFS-652 Phase 1).
//
// Ordnet mehrere Felder an und setzt die Anordnung EINMAL — alle enthaltenen
// <pfs-field> erben sie über `--pfs-field-label`. SHADOW-WC (nicht globales
// Stylesheet!): die Cut-App-Cards rendern in einer Shadow-Root, ein Sheet in
// document.head erreicht sie NICHT. `:host`-Styles hingegen wirken in JEDER
// Shadow. Die Felder werden via `<slot>` (display:contents) zu Grid-Items des
// Host-Grids — die validierte Spike-Konstellation, nur mit Shadow-Indirektion.
//
//   <pfs-field-group data-layout="aligned">
//     <pfs-field data-label="Name"><pfs-text-field/></pfs-field>
//     <pfs-field data-label="Ort" data-span="all"><pfs-text-field/></pfs-field>
//   </pfs-field-group>
//
// data-layout: stacked (default) | inline | aligned. inline & aligned sind
// intrinsisch responsiv (fallen bei schmalem Container auf stacked zurück).
//
// container-type auf dem Host → die Felder (slotted) fragen IHN via @container ab.
// aligned ist IMMER Grid: bei schmalem Container werden die Felder zu block +
// grid-column:1/-1 (volle Zeile = gestapelt), bei breitem zu display:contents
// (Label|Control in geteilten Spalten). So macht das FELD den responsiven Flip —
// der Host muss sich nicht selbst abfragen (@container kann das nicht).
import { defineComponent } from "../../composition/define-component.js";
import { withShadow } from "../../composition/with-shadow.js";

const STYLES = `
:host { display: block; }
:host([data-layout="stacked"]) {
  display: flex; flex-direction: column; gap: var(--pfs-space-field, 0.75rem);
  --pfs-field-label: stacked;
}
:host([data-layout="inline"]) {
  display: flex; flex-direction: column; gap: var(--pfs-space-field, 0.75rem);
  container-type: inline-size; --pfs-field-label: inline;
}
:host([data-layout="aligned"]) {
  display: grid; grid-template-columns: max-content minmax(0, 1fr);
  gap: var(--pfs-space-field, 0.75rem) var(--pfs-space-col, 0.5rem); align-items: center;
  container-type: inline-size; --pfs-field-label: aligned;
}
`;

/** Typisierte Consumer-Sicht. */
export interface PfsFieldGroupElement extends HTMLElement {
  layout: string;
}

defineComponent({
  tagName: "pfs-field-group",
  mixins: [withShadow()],
  // eager: wird (wie <pfs-field>) synchron von Cut-App-WCs konsumiert.
  eager: true,
  created(host) {
    Object.defineProperty(host, "layout", {
      configurable: true,
      get: () => host.getAttribute("data-layout") ?? "stacked",
      set: (v: unknown) => host.setAttribute("data-layout", v == null ? "stacked" : String(v)),
    });
  },
  render(host, ctx) {
    const root = ctx.shadowRoot;
    if (!root) return;
    if (!host.hasAttribute("data-layout")) host.setAttribute("data-layout", "stacked");
    const style = document.createElement("style");
    style.textContent = STYLES;
    root.append(style, document.createElement("slot"));
  },
});
